import { useState, useEffect } from 'react'
import { ScrollView, View, Text, Platform } from 'react-native'
import { BackButton } from '@/src/common/components/BackButton'
import { Button } from '@/src/common/components/Button'
import { Routes } from '@/src/common/enums/routes'
import { Input } from '@/src/common/components/Input'
import { useRouter } from 'expo-router'
import { useCardApplicationReferralCodeStore } from '@/src/common/store/useCardApplicationReferralCodeStore'
import { useLazyQuery } from '@apollo/client'
import { graphql } from '@api/mesa'
import { useLogError } from '@/src/common/hooks/useLogError'
import Toast from 'react-native-toast-message'
import { useSardine } from '@/src/services/Sardine'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { mesaColorNames, typography } from '@/src/common/theme'
import { cardApplicationStyles as common } from './common'
import * as E2E from '../../e2e'

export function OfferCode() {
  const router = useRouter()
  const logError = useLogError()
  const { setReferralCode, referralCode = '' } = useCardApplicationReferralCodeStore()
  const [isFocused, setIsFocused] = useState(false)
  const [isOfferCodeValid, setIsOfferCodeValid] = useState<boolean | null>(null)
  const { submitData, updateOptions } = useSardine()

  useEffect(() => {
    void updateOptions({ flow: 'application-offer-code' })
  }, [updateOptions])

  function onContinuePress() {
    void submitData()
    setReferralCode(referralCode.trim())
    router.push(Routes.FindYourHome.route)
  }

  const showErrorToast = (text1 = 'Please enter a valid code to proceed') => {
    Toast.show({
      text1,
      type: 'error',
    })
  }

  const [validateOfferCode, { loading }] = useLazyQuery(
    graphql(`
      query validateOfferCodeForCardApplication($offerCode: String!) {
        validateOfferCodeForCardApplication(offerCode: $offerCode) {
          referralCode
          isValid
          message
        }
      }
    `),
    {
      onError(error) {
        logError(error)
        showErrorToast()
        setIsOfferCodeValid(false)
      },
      onCompleted: (data) => {
        if (data.validateOfferCodeForCardApplication.isValid) {
          Toast.show({
            text1:
              data.validateOfferCodeForCardApplication.message ||
              `Offer code ${referralCode.trim()} accepted`,
            type: 'success',
          })
          setIsOfferCodeValid(true)
        } else {
          showErrorToast(data.validateOfferCodeForCardApplication.message ?? undefined)
          setIsOfferCodeValid(false)
        }
      },
    },
  )

  return (
    <CardApplicationWrapper>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={Platform.OS === 'web' && { overflow: 'visible' }}
      >
        <BackButton
          href={Routes.CardOnboarding.route}
          text="Back"
          testID={E2E.Config.Ids.Press.OfferCodeBack}
        />
        <View style={common.headerText}>
          <Text style={typography['heading/heading2']}>Have an offer code?</Text>
        </View>
        <Text style={[typography['paragraph/md'], common.instructionText]}>
          If so please enter it now, if not no worries. You can submit your application without one.
        </Text>
        <Input
          autoComplete="off"
          autoCorrect={false}
          containerStyle={{ paddingHorizontal: 0 }}
          inputContainerStyle={{
            borderColor: isFocused
              ? mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9']
              : mesaColorNames['Greyscale/300 #CDD4D5'],
          }}
          placeholder="Promo code"
          value={referralCode}
          sardineId="offerCode"
          onChangeText={setReferralCode}
          onFocus={() => {
            setIsFocused(true)
            setIsOfferCodeValid(null)
          }}
          onBlur={() => {
            setIsFocused(false)
            referralCode &&
              void validateOfferCode({ variables: { offerCode: referralCode.trim() } })
          }}
          returnKeyType="done"
          testID={E2E.Config.Ids.Input.OfferCode}
          {...(typeof isOfferCodeValid === 'boolean'
            ? {
                info: {
                  props: { testID: E2E.Config.Ids.Text.OfferCodeStatus },
                  ...(isOfferCodeValid
                    ? { success: `Offer code ${referralCode} accepted` }
                    : { error: 'Please enter a valid code to proceed' }),
                },
              }
            : undefined)}
        />
      </ScrollView>
      <View style={{ marginBottom: 10, marginTop: 10 }}>
        <Button
          title="Continue"
          onPress={onContinuePress}
          disabled={loading || (!!referralCode && !isOfferCodeValid)}
          testID={E2E.Config.Ids.Press.OfferCodeContinue}
        />
      </View>
    </CardApplicationWrapper>
  )
}
